import { Typography } from "@mui/material";
import { useState, useEffect, useCallback, useRef } from "react";
import { LandingNavbar } from "../../components/LandingNavbar";
import {
  StyledDensityHardwareContainer,
  StyledLandingPageWrapper,
} from "./landing.styled";
import { useWindowDimensions } from "../../utils/hooks/windowDimensionsHook";
import { Footer } from "../../components/Landing/Footer";
import { useDispatch } from "react-redux";
import Decks4 from "../../assets/decks-4.svg";
import NoWatermark from "../../assets/no-watermark.svg";
import { Header } from "../../components/Header";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import { api } from "../../api/base";
import { decodeToken } from "react-jwt";
import { analyticsEvent } from "../../redux/actions/analytics";
import { setLoginModalOpen } from "../../redux/actions/app";
import { StyledAuthButton } from "../../components/micro/micro.styled";
import HardwareDensity from "../../assets/Hardware-with-shadow.png";
import { useUserContext } from "../../context/UserContext";
import { UpgradeModal } from "../Profile/modal/UpgradeModal";
import { SinglePlanItem, SubscriptionClass } from "../../types/subscription";
import { useSnackBar } from "../../context/SnackBar";
import { ConfirmRenewalModal } from "../../components/modals/ConfirmRenewal";

import { isBFSale } from "../../utils/sales";
import {getDownloadLink, useDeviceInfo} from "../../utils/hooks/useDeviceInfo";
import { isProPlan } from "../../utils/plan.utils";

export const PricingPage = (props: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { width } = useWindowDimensions();

  const [visible] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { showSnackBar } = useSnackBar();
  const { user, setPlan, isUserLoggedIn, fetchingUserData, isPasswordSet, getProfile, fetchPricingContext, pricingContext } =
    useUserContext();
  const [renewalModalOpen, setRenewalModalOpen] = useState<boolean>(false);
  const [fetchingPricingContext, setFetchingPricingContext] = useState<boolean>(false);

  type upgradeDataType = {
    open: boolean;
    url: string;
    handleCloseModal: () => void;
    price: string;
    additionalText: string;
    title?: string;
    couponCode?: string; // Add the couponCode property
  };
  const [upgradeData, setUpgradeData] = useState({} as upgradeDataType);
  const [loadingUpgrade, setLoadingUpgrade] = useState(false);
  const [loadingPurchase, setLoadingPurchase] = useState(false);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
  const [plans, setPlans] = useState<SinglePlanItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionClass>(
    {} as SubscriptionClass,
  );
  const [rtoDiscount, setRtoDiscount] = useState<{
    discountAmount: number;
    monthsPaid: number;
    monthlyRate: number;
    isRTOUser: boolean;
    blackFridayPrice?: number;
    finalPrice?: number;
  }>({
    discountAmount: 0,
    monthsPaid: 0,
    monthlyRate: 0,
    isRTOUser: false
  });

  const upgradeButtonRef = useRef<any>(null);
  const hardwareButtonRef = useRef<any>(null);
  const deviceInfo = useDeviceInfo();
  
  // Check if user is a Pro user using the utility function
  const isUserPro = isProPlan(user?.plan, user?.license_type);

  const oldHardwarePrice = 450; // Always show original price of $450 for Fall Sale
  const newHardwarePrice = isUserPro ? 250 : 350;
  const newHardwarePriceText = isUserPro ? "$250 - One Time" : "$350";
  
  // Debug logging
  useEffect(() => {
    console.log('[Pricing Page] User state:', {
      email: user?.email,
      plan: user?.plan,
      license_type: user?.license_type,
      isUserPro,
      fetchingUserData,
      isUserLoggedIn,
      hasToken: !!localStorage.getItem('access_token'),
      pricingContext,
      fullUser: user
    });
    
    // Also log when component mounts
    console.log('[Pricing Page] Component mounted, checking user context...');
    
    // Force a profile refresh if we have a token but no user data
    if (localStorage.getItem('access_token') && !user?.email && !fetchingUserData) {
      console.log('[Pricing Page] Have token but no user data, forcing refresh...');
      getProfile(true, true); // Force refresh with skipAutoCreate=true
    }
  }, [user, isUserPro, fetchingUserData, isUserLoggedIn, pricingContext]);

  // Fetch pricing context when component mounts or user changes
  useEffect(() => {
    const fetchContext = async () => {
      if (localStorage.getItem('access_token') && !fetchingPricingContext) {
        console.log('[Pricing Page] Fetching pricing context...');
        setFetchingPricingContext(true);
        try {
          const fetchedContext = await fetchPricingContext();
          console.log('[Pricing Page] Pricing context fetched:', fetchedContext);
        } finally {
          setFetchingPricingContext(false);
        }
      }
    };
    
    fetchContext();
  }, [user?.email]); // Re-fetch when user email changes

  const getPlans = async (): Promise<void> => {
    try {
      const { data }: AxiosResponse<any, any> =
        await api.get("chargebee/plans");
      setPlans(data);
    } catch (err) {}
  };

  const renewSubscription = async (): Promise<void> => {
    try {
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/renew-subscription",
        {
          planId: subscriptions.id,
          trialEnd: subscriptions.trial_end,
          status: subscriptions.status,
        },
      );
      showSnackBar(
        "Successfully reactivated subscription.",
        "success",
        "center",
        2000,
        false,
        "auto",
        "95px",
      );
      await getSubscriptions();
      setRenewalModalOpen(false);
    } catch (err: any) {
      const { response } = err;
      showSnackBar(response.data.error_msg, "error");
    }
  };

  const handleSubscriptionRenewal = async (): Promise<void> => {
    renewSubscription();
  };

  const getSubscriptions = async (): Promise<void> => {
    try {
      const { data }: AxiosResponse<any, any> = await api.get(
        "chargebee/subscriptions",
      );
      setSubscriptions(data);
      setPlan({ planId: data.id, planName: data.item_price_id });
    } catch (err) {}
  };

  const getRTODiscount = async (): Promise<void> => {
    try {
      // During Fall Sale, fetch Fall Sale specific RTO discount
      if (isBFSale()) {
        const { data } = await api.get("chargebee/black-friday-rto-discount");
        setRtoDiscount(data);
        console.log('[Pricing Page] Fall Sale RTO discount fetched:', data);
      } else {
        // Regular RTO discount
        const { data } = await api.get("chargebee/rto-discount");
        setRtoDiscount(data);
        console.log('[Pricing Page] Regular RTO discount fetched:', data);
      }
    } catch (err) {
      console.error('[Pricing Page] Failed to get RTO discount:', err);
    }
  };
  const handleRenewalConfirmation = () => {
    setRenewalModalOpen(true);
  };

  useEffect(() => {
    if (fetchingUserData) {
      setIsLoadingSubscriptions(true);
      return;
    }

    const getPlansAndSubscriptions = async () => {
      try {
        // Run all requests in parallel for better performance
        await Promise.all([
          getPlans(),
          getSubscriptions(),
          getRTODiscount()
        ]);
      } finally {
        setIsLoadingSubscriptions(false);
      }
    };

    if (isUserLoggedIn) {
      getPlansAndSubscriptions();
    } else {
      setIsLoadingSubscriptions(false);

      if (searchParams.has("purchase") && !fetchingUserData) {
        dispatch(setLoginModalOpen(true));
      }
    }
  }, [fetchingUserData, isUserLoggedIn]);

  useEffect(() => {
    if (fetchingUserData || !isUserLoggedIn || isLoadingSubscriptions) {
      return;
    }

    const purchaseParam = searchParams.get("purchase");
    console.log('[Pricing Page] Purchase param check:', {
      purchaseParam,
      isUserLoggedIn,
      fetchingUserData,
      isLoadingSubscriptions,
      searchParams: searchParams.toString(),
      userPlan: user?.plan,
      isUserPro,
      rtoDiscount
    });

    switch (purchaseParam) {
      case "upgrade":
        upgradeButtonRef.current.click();
        break;
      case "hardware":
        hardwareButtonRef.current.click();
        break;
      case "density-pro":
        // Check if user already has Pro or RTO before triggering purchase
        console.log('[Pricing Page] Checking if user can purchase Pro...');
        if (isUserPro || rtoDiscount.isRTOUser) {
          console.log('[Pricing Page] User already has Pro/RTO, showing warning');
          showSnackBar(
            "You already have an active Pro or Rent-to-Own subscription",
            "error",
            "center",
            5000
          );
          // Remove the purchase param from URL to prevent re-triggering
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete("purchase");
          navigate(`/pricing${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`, { replace: true });
        } else {
          console.log('[Pricing Page] User can purchase Pro, triggering buyPro');
          buyPro();
        }
        break;
      case "rto":
        // Check if user already has Pro or RTO before triggering purchase
        console.log('[Pricing Page] Checking if user can purchase RTO...');
        if (isUserPro || (rtoDiscount.isRTOUser && user?.rto?.status !== 'paused')) {
          console.log('[Pricing Page] User already has Pro/RTO, showing warning');
          showSnackBar(
            "You already have an active Pro or Rent-to-Own subscription",
            "error",
            "center",
            5000
          );
          // Remove the purchase param from URL to prevent re-triggering
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete("purchase");
          navigate(`/pricing${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`, { replace: true });
        } else {
          console.log('[Pricing Page] User can purchase RTO, triggering buyRentToOwn');
          buyRentToOwn();
        }
        break;
      default:
        break;
    }
  }, [fetchingUserData, isUserLoggedIn, isLoadingSubscriptions, searchParams, isUserPro, rtoDiscount, user]);

  useEffect(() => {
    dispatch(
      analyticsEvent("EVENT", "Pricing Page Visited", {
        page: "Pricing Page",
      }),
    );
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "scroll";
    }
    return () => {
      document.body.style.overflowY = "scroll";
    };
  }, [isOpen]);

  const upgradeButtonText = () => {
    if (subscriptions.status === "non_renewing") {
      return "Reactivate";
    } else if (subscriptions.status === "cancelled") {
      return "Renew";
    }
    return "Upgrade";
  };

  const isUpgradeButtonDisabled = () => {
    if (fetchingUserData || isLoadingSubscriptions || loadingUpgrade) {
      return true;
    }

    if (
      Object.keys(subscriptions).length == 0 ||
      subscriptions?.item_price_id === "Free-USD-Monthly"
    ) {
      return false;
    }

    if (
      subscriptions.status === "non_renewing" ||
      subscriptions.status === "cancelled"
    ) {
      return false;
    }

    // TODO: More conditions?
    return true;
  };

  const isHardwareButtonDisabled = () => {
    return fetchingUserData || loadingPurchase;
  };

  const buyHardwarePlan = async (): Promise<void> => {
    if (!isUserLoggedIn && !fetchingUserData) {
      getDensity(false, true);
      return;
    }
    try {
      const token = localStorage.getItem("access_token");
      //@ts-ignore
      const decodedToken = decodeToken(token);
      //@ts-ignore
      const email = decodedToken?.email;
      console.log('Attempting hardware checkout for email:', email);
      
      // Determine which coupon to use for the Fall Sale sale
      const bfCoupon = isBFSale() ? (isUserPro ? "BF2025-existing" : "BF2025") : "";
      
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/checkout-hardware",
        {
          redirect_url: `${process.env.REACT_APP_URL}/account/upgrade-success?plan=hardware-pro-bundle`,
          email,
          ...(isBFSale() && { coupon: isUserPro ? "BF2025-existing" : "BF2025" }),
        },
      );
      console.log('Hardware checkout successful, redirecting to:', data.url);
      navigate(`/account/checkout`, {
        state: {
          url: data.url,
          additionalText: "Hardware + Pro Bundle",
          title: "Density",
          cancelUrl: "/pricing",
          price: "", // Remove the price text
          couponCode: bfCoupon, // Pass the coupon code as a separate prop
        },
      });
    } catch (err: any) {
      console.error('Hardware checkout failed:', err);
      console.error('Error response:', err.response?.data);
      showSnackBar(
        err.response?.data?.message || 'Failed to initiate hardware checkout. Please try again.',
        'error'
      );
    }
    setLoadingPurchase(false);
  };

  const getDensity = (isUpgrade = false, isHardware = false) => {
    if (props.getDensity) {
      props.getDensity();
    } else {
      localStorage.removeItem("landing");

      const ref = searchParams.get("ref");
      const navParams = `${isUpgrade ? "?upgrade=true" : ""}${isHardware ? "?plan=hardware" : ""}`;

      if (user?.email && !isPasswordSet) {
        navigate(`/onboarding/5/${ref || ""}${navParams}`);
        return;
      }

      if (ref) {
        navigate(`/onboarding/0/${ref}${navParams}`);
      } else {
        navigate(`/onboarding/1${navParams}`);
      }
    }
  };

  const handleDownload = () => {
    dispatch(
      analyticsEvent("EVENT", "Download Density App", {
        "OS Downloaded": "Universal",
        location: "Pricing Page",
      }),
    );

    api
      .post("/auth/download", {
        email: user?.email,
        os: "universal",
      })
      .then((res) => {
        // console.log(res);
      })
      .catch((err) => {
        // console.log(err);
      });
    navigate("/onboarding/6?instructions=true");
  };

  const handleDownloadFree = () => {
    localStorage.setItem("onboarding_in_progress", "true");
    dispatch(
      analyticsEvent("CLICK", "Download Free Button clicked", {
        page: "Pricing Page",
      }),
    );
    if (isUserLoggedIn && !fetchingUserData) {
      handleDownload();
    } else {
      getDensity();
    }
  };

  const handleUpgrade = async () => {
    setLoadingUpgrade(true);
    const upgradeButtonTextValue = upgradeButtonText();
    dispatch(
      analyticsEvent("CLICK", `${upgradeButtonTextValue} button clicked`, {
        location: "Pricing Page",
      }),
    );

    if (isUserLoggedIn && upgradeButtonTextValue === "Upgrade") {
      const buyPlan = async (): Promise<void> => {
        try {
          const { data }: AxiosResponse<any, any> = await api.post(
            "chargebee/checkout",
            {
              item_price_id: "Premium-USD-Monthly",
              // unit_price: plan.price,
              redirect_url: `${process.env.REACT_APP_URL || window.location.origin}/account${searchParams.get("fromDesktop") ? "?fromDesktop=true" : ""}`,
              coupon: isBFSale() ? "BF2025" : "",
            },
          );
          const handleCloseModal = () => {
            setUpgradeData({ ...upgradeData, open: false });
            setLoadingUpgrade(false);
            navigate("/onboarding/6");
          };
          setUpgradeData({
            ...upgradeData,
            url: data.url,
            price: "$15/month", // Keep the original price text
            open: true,
            handleCloseModal,
            couponCode: isBFSale() ? "BF2025" : "", // Pass the coupon code as a separate prop
          });
          setLoadingUpgrade(false);
        } catch (err) {}
      };
      try {
        await buyPlan();
      } catch (err) {
        console.error(err);
        setLoadingUpgrade(false);
      }
      return;
    } else {
      if (
        isUserLoggedIn &&
        (upgradeButtonTextValue === "Reactivate" ||
          upgradeButtonTextValue === "Renew")
      ) {
        handleRenewalConfirmation();
      } else {
        getDensity(true);
      }
    }
    setLoadingUpgrade(false);
  };

  const buyPro = async (): Promise<void> => {
    // Wait for user data to be loaded
    if (fetchingUserData) {
      console.log("Waiting for user data to load...");
      showSnackBar("Loading user data, please wait...", "info");
      return;
    }

    // Ensure user is logged in
    if (!isUserLoggedIn) {
      console.log("User not logged in, cannot proceed with checkout");
      showSnackBar("Please log in to continue", "error");
      return;
    }

    // Check if user already has Pro or RTO
    if (isUserPro || rtoDiscount.isRTOUser) {
      console.log("User already has Pro/RTO, preventing duplicate purchase");
      showSnackBar(
        "You already have an active Pro or Rent-to-Own subscription",
        "error",
        "center",
        5000
      );
      return;
    }

    // Add a small delay to ensure everything is loaded
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      console.log("buyPro called - initiating Pro checkout");
      const payload = {
        item_price_id: "density-pro-v1-charge-USD",
        redirect_url: `${process.env.REACT_APP_URL || window.location.origin}/account/upgrade-success?plan=density-pro-v1${searchParams.get("fromDesktop") ? "&fromDesktop=true" : ""}`,
        ...(searchParams.get("coupon") && { coupon: searchParams.get("coupon") }) || (isBFSale() && { coupon: "BF2025" }),
      };
      console.log("Sending payload:", payload);
      
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/checkout",
        payload,
      );
      console.log("Received checkout URL:", data.url);
      
      const handleCloseModal = () => {
        setUpgradeData({ ...upgradeData, open: false });
      };
      
      // Determine which coupon is being used
      const couponUsed = searchParams.get("coupon") || (isBFSale() ? "BF2025" : "");
      
      setUpgradeData({
        ...upgradeData,
        url: data.url,
        price: "Version 1", // Keep the original price text
        title: "Density Pro",
        open: true,
        handleCloseModal,
        couponCode: couponUsed, // Pass the coupon code as a separate prop
      });
    } catch (err: any) {
      console.error("Failed to initiate Pro checkout:", err);
      // Check if the error is due to already having a subscription
      if (err.response?.status === 400 && err.response?.data?.message) {
        showSnackBar(err.response.data.message, "error");
      } else {
        showSnackBar("Failed to initiate checkout. Please try again.", "error");
      }
    }
  };

  const buyRentToOwn = async (): Promise<void> => {
    // Wait for user data to be loaded
    if (fetchingUserData) {
      console.log("Waiting for user data to load...");
      showSnackBar("Loading user data, please wait...", "info");
      return;
    }

    // Ensure user is logged in
    if (!isUserLoggedIn) {
      console.log("User not logged in, cannot proceed with checkout");
      showSnackBar("Please log in to continue", "error");
      return;
    }

    // Check if user has a paused RTO subscription
    const hasPausedRTO = user?.rto && user?.rto?.status === 'paused';
    
    if (hasPausedRTO) {
      // If RTO is paused, we need to resume it
      console.log("User has paused RTO, attempting to resume");
      
      if (user.rto?.subscriptionId) {
        try {
          const { data }: AxiosResponse<any, any> = await api.post(
            "chargebee/resume-rto",
            {
              itemId: user.rto.subscriptionId,
            },
          );
          showSnackBar(
            "Successfully resumed your Rent-to-Own subscription.",
            "success",
            "center",
            2000,
            false,
            "auto",
            "95px",
          );
          // Refresh user profile data
          await getProfile(false, true); // Use skipAutoCreate=true
          // Navigate to account page to see the updated status
          navigate("/account");
        } catch (err: any) {
          const { response } = err;
          showSnackBar(response?.data?.error_msg || "Failed to resume subscription", "error");
        }
      } else {
        showSnackBar("Unable to find your paused subscription. Please contact support.", "error");
      }
      return;
    }

    // Check if user already has Pro or active RTO
    if (isUserPro || (rtoDiscount.isRTOUser && user?.rto?.status !== 'paused')) {
      console.log("User already has Pro/RTO, preventing duplicate purchase");
      showSnackBar(
        "You already have an active Pro or Rent-to-Own subscription",
        "error",
        "center",
        5000
      );
      return;
    }

    try {
      console.log("buyRentToOwn called - initiating Rent to Own checkout");
      const payload = {
        plan: "rto",
        // Use the Fall Sale RTO plan ID when BF sale is active
        item_price_id: isBFSale() ? "rto-v1-bf2025-USD-Monthly" : "rto-v1-15usd-14cycles-USD-Monthly",
        redirect_url: `${process.env.REACT_APP_URL || window.location.origin}/account/upgrade-success?plan=rto${searchParams.get("fromDesktop") ? "&fromDesktop=true" : ""}`,
        cancel_url: `${process.env.REACT_APP_URL || window.location.origin}/pricing`,
      };
      console.log("Sending payload:", payload);
      
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/checkout",
        payload,
      );
      console.log("Received checkout URL:", data.url);
      
      const handleCloseModal = () => {
        setUpgradeData({ ...upgradeData, open: false });
      };
      
      setUpgradeData({
        ...upgradeData,
        url: data.url,
        // Show the correct price based on whether it's Fall Sale or not
        price: isBFSale() ? "$7.86/month for 14 months" : "$15/month for 14 months",
        title: "Density Pro - Rent to Own",
        open: true,
        handleCloseModal,
      });
    } catch (err: any) {
      console.error("Failed to initiate Rent to Own checkout:", err);
      console.error("Error response:", err.response?.data);
      
      // Check if the error is due to already having a subscription
      if (err.response?.status === 400 && err.response?.data?.message) {
        showSnackBar(err.response.data.message, "error");
      } else {
        // Show specific error message if available
        const errorMessage = err.response?.data?.message ||
                            err.response?.data?.error ||
                            "Failed to initiate checkout. Please try again.";
        showSnackBar(errorMessage, "error");
      }
    }
  };

  const handlePurchasePro = () => {
    dispatch(
      analyticsEvent("CLICK", "Purchase Pro button clicked", {
        page: "Pricing Page",
        isRTOUser: rtoDiscount.isRTOUser,
      }),
    );
    
    if (!isUserLoggedIn && !fetchingUserData) {
      // Redirect to onboarding with plan parameter to maintain context
      const ref = searchParams.get("ref");
      if (ref) {
        navigate(`/onboarding/0/${ref}?plan=density-pro${isBFSale() ? "&coupon=BF2025" : ""}`);
      } else {
        navigate(`/onboarding/1?plan=density-pro${isBFSale() ? "&coupon=BF2025" : ""}`);
      }
      return;
    }
    
    // If user is on RTO, trigger buyout instead of regular purchase
    if (rtoDiscount.isRTOUser) {
      handleRTOBuyout();
    } else {
      buyPro();
    }
  };

  const handleRentToOwn = () => {
    dispatch(
      analyticsEvent("CLICK", "Rent to Own button clicked", {
        page: "Pricing Page",
      }),
    );
    dispatch(
      analyticsEvent("CLICK", "Rent to Own Plan Selected", {
        page: "Pricing Page",
        plan_details: "RTO Plan Name/ID - TODO: Populate dynamically",
      }),
    );
    
    if (!isUserLoggedIn && !fetchingUserData) {
      // Redirect to login/signup with plan parameter to maintain context
      const ref = searchParams.get("ref");
      if (ref) {
        navigate(`/onboarding/0/${ref}?plan=rto`);
      } else {
        navigate(`/onboarding/1?plan=rto`);
      }
      return;
    }
    
    buyRentToOwn();
  };

  const handleRTOBuyout = async (): Promise<void> => {
    try {
      console.log("handleRTOBuyout called - initiating RTO buyout");
      dispatch(
        analyticsEvent("CLICK", "Rent to Own Buyout Initiated", {
          page: "Pricing Page",
          plan_details: "RTO Buyout", // Specific to buyout from pricing page
        }),
      );
      const payload = {
        redirect_url: `${process.env.REACT_APP_URL || window.location.origin}/account/upgrade-success?plan=rto-buyout`,
        cancel_url: `${process.env.REACT_APP_URL || window.location.origin}/pricing`
      };
      
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/rto-buyout",
        payload
      );
      
      if (data.url) {
        const handleCloseModal = () => {
          setUpgradeData({ ...upgradeData, open: false });
        };
        
        setUpgradeData({
          ...upgradeData,
          url: data.url,
          price: "",
          title: "Density Pro",
          open: true,
          handleCloseModal,
        });
      }
    } catch (err) {
      console.error("Failed to initiate RTO buyout:", err);
      showSnackBar("Failed to initiate buyout. Please try again.", "error");
    }
  };

  const handlePurchase = async () => {
    setLoadingPurchase(true);
    dispatch(
      analyticsEvent("CLICK", "Purchase button clicked", {
        page: "Pricing Page",
      }),
    );
    await buyHardwarePlan();
  };

  return (
    <>
      <StyledLandingPageWrapper className="landing-page-wrapper">
        {isUserLoggedIn ? (
          <Header />
        ) : (
          <LandingNavbar
            visible={visible}
            ww={width}
            getDensity={() => {
              return handlePurchase();
            }}
          />
        )}
        <main style={{ maxWidth: 1360, margin: "auto" }}>
          <StyledDensityHardwareContainer>
            <div className="mb-[72px] mt-[72px]">
              <Typography
                className="density-hero-heading hardware-density-heading"
                variant="h1"
                component="h1"
                sx={{
                  background:
                    "linear-gradient(182.83deg, rgba(218, 220, 240, 0.8) 12.57%, rgba(168, 171, 219, 0.5) 88.12%)",
                  "-webkit-background-clip": {
                    md: "text",
                    xs: "text",
                  },
                  "-webkit-text-fill-color": {
                    md: "transparent",
                    xs: "transparent",
                  },
                }}
              >
                Pricing
              </Typography>
            </div>
          </StyledDensityHardwareContainer>

          <section className="">
            <div className="flex flex-wrap gap-4">
              <div className="relative box-border flex w-[100%] flex-col rounded-[30px] bg-[#FFFFFF0D] p-[48px] pb-[36px] text-center md:w-[calc(50%-0.5rem)] h-[471px]">
                <div className="flex flex-col h-full">
                  <div>
                    <div className="flex w-[100%] flex-row justify-between pb-[24px]">
                      <div className="flex flex-col">
                        <h2 className="whitespace-nowrap pb-[8px] text-left font-['PPRightGrotesk-WideMedium'] text-[34px] font-black leading-[27.42px] !text-primary-purple">
                          {"Basic"}
                        </h2>
                        <p className="font-sm text-left font-inter text-base leading-[24px] tracking-wide !text-primary-purple">
                          Free
                        </p>
                      </div>
                      <div>
                        <img src={Decks4} alt="Decks4" className="w-[108px]" />
                      </div>
                    </div>

                    <div className="text-left font-inter text-sm font-medium leading-6 tracking-[0.0125rem] !text-base-gray">
                      <li className="disc-list">
                        Complete use of the{" "}
                        <span
                          className="cursor-pointer text-left text-center tracking-wide underline"
                          style={{ color: "#575F9E" }}
                          onClick={() => {
                            navigate("/");
                          }}
                        >
                          Density DAW
                        </span>{" "}
                        for free
                      </li>
                      <div className="pl-6">
                        <p>– Point & Click to create a set</p>
                        <p>– Capture a live set by connecting your decks to your laptop</p>
                      </div>
                      <li className="pb-2">Master any Density recording</li>
                      <p className="mt-4">Limitation:</p>
                      <li className="disc-list">
                        On export there is an unobtrusive audio watermark that says, "mixed with Density"
                      </li>
                    </div>
                  </div>
                  <div className="mt-auto w-[100%]">
                  <a
                    href={`${isUserLoggedIn && !fetchingUserData ? getDownloadLink(deviceInfo) : ""}`}
                    download={false}
                  >
                    <StyledAuthButton
                      onClick={handleDownloadFree}
                      variant={"outlined"}
                      className="min-w-[100%]"
                      disabled={fetchingUserData}
                      sx={{
                        m: 0,
                        minWidth: "100%",
                        background: "transparent",
                        borderColor: "rgba(252, 252, 252, 0.3)",
                        letterSpacing: "0.5px",
                        fontSize: "11px",
                        color: "rgba(252, 252, 252, 0.8)",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        height: 44,
                        "&:hover": {
                          borderColor: "rgba(252, 252, 252, 0.5)",
                          background: "rgba(255, 255, 255, 0.05)",
                        }
                      }}
                    >
                      DOWNLOAD FREE
                    </StyledAuthButton>
                  </a>
                  {/* Invisible spacer to match the height of "Future upgrades" text in Pro block */}
                  <div className="mt-2 text-xs opacity-0 pointer-events-none">
                    &nbsp;
                  </div>
                </div>
              </div>
            </div>

              <div className="relative box-border flex w-[100%] flex-col rounded-[30px] bg-[#FFFFFF0D] p-[48px] pb-[36px] text-center md:w-[calc(50%-0.5rem)] h-[471px]">
                <div className="flex flex-col h-full">
                  <div>
                    <div className="flex w-[100%] flex-row justify-between pb-[24px]">
                      <div className="flex flex-col">
                        <h2 className="whitespace-nowrap pb-[8px] text-left font-['PPRightGrotesk-WideMedium'] text-[34px] font-black leading-[27.42px] !text-primary-purple">
                          {"Pro"}
                        </h2>
                        <p className="font-sm text-left font-inter text-base leading-[24px] tracking-wide !text-primary-purple">
                          {isBFSale() ? (
                            <>
                              <span style={{ textDecoration: 'line-through', color: 'red', opacity: 0.6 }}>$200</span>
                              <span className="ml-2" style={{ color: 'green' }}>$100 one time</span><br />
                              OR <span style={{ textDecoration: 'line-through', color: 'red', opacity: 0.6 }}>$15</span>
                              <span className="ml-2" style={{ color: 'green' }}>$7.86/mth Rent-to-Own for 14 months. Cancel any time.</span>
                            </>
                          ) : (
                            rtoDiscount.isRTOUser && rtoDiscount.discountAmount > 0 ? (
                              <>
                                <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>$210</span>
                                <span className="ml-2">
                                  ${210 - rtoDiscount.discountAmount} one time
                                </span><br />
                                <span style={{ fontSize: '14px' }}>
                                  Rent-to-Own discount applied
                                </span>
                              </>
                            ) : (
                              <>
                                $200 one time<br />
                                OR $15/mth Rent-to-Own for 14 months. Cancel any time.
                              </>
                            )
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-left font-inter text-sm font-medium leading-6 tracking-[0.0125rem] !text-base-gray">
                      <li className="disc-list">Permanent license to Density Pro v1 without the watermark</li>
                      <div className="-mx-[48px] mt-6 w-[calc(100%+96px)]">
                        <img src={NoWatermark} alt="no-watermark" className="" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto w-[100%]">
                    <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                      <StyledAuthButton
                        onClick={handlePurchasePro}
                        variant={"contained"}
                        color="primary"
                        className="min-w-[100%]"
                        disabled={fetchingUserData || fetchingPricingContext || isLoadingSubscriptions || (isProPlan(user?.plan, user?.license_type) && !rtoDiscount.isRTOUser) || (pricingContext && !pricingContext.canUpgrade && !rtoDiscount.isRTOUser) || false}
                        sx={{
                          flex: 1,
                          height: 44,
                          margin: 0,
                          padding: "15px",
                          backgroundColor: (isProPlan(user?.plan, user?.license_type) && !rtoDiscount.isRTOUser) ? "#666" : "#c1c5dd",
                          fontSize: "11px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          color: (isProPlan(user?.plan, user?.license_type) && !rtoDiscount.isRTOUser) ? "#999" : "#020203",
                          "&:hover": {
                            backgroundColor: (isProPlan(user?.plan, user?.license_type) && !rtoDiscount.isRTOUser) ? "#666" : "#a8add1",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#666",
                            color: "#999",
                          }
                        }}
                      >
                        {rtoDiscount.isRTOUser ? 'BUY NOW' : (isProPlan(user?.plan, user?.license_type) ? 'ALREADY PURCHASED' : 'PURCHASE')}
                      </StyledAuthButton>
                      <StyledAuthButton
                        onClick={handleRentToOwn}
                        variant={"contained"}
                        className="min-w-[100%]"
                        disabled={fetchingUserData || fetchingPricingContext || isLoadingSubscriptions || isProPlan(user?.plan, user?.license_type) || (rtoDiscount.isRTOUser && !(user?.rto && user?.rto?.status === 'paused')) || (pricingContext && pricingContext.hasMultipleSubscriptions) || false}
                        sx={{
                          flex: 1,
                          height: 44,
                          margin: 0,
                          padding: "15px",
                          backgroundColor: (isProPlan(user?.plan, user?.license_type) || (rtoDiscount.isRTOUser && !(user?.rto && user?.rto?.status === 'paused'))) ? "#666" : "#c1c5dd",
                          fontSize: "11px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          color: (isProPlan(user?.plan, user?.license_type) || (rtoDiscount.isRTOUser && !(user?.rto && user?.rto?.status === 'paused'))) ? "#999" : "#020203",
                          "&:hover": {
                            backgroundColor: (isProPlan(user?.plan, user?.license_type) || (rtoDiscount.isRTOUser && !(user?.rto && user?.rto?.status === 'paused'))) ? "#666" : "#a8add1",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#666",
                            color: "#999",
                          }
                        }}
                      >
                        {user?.rto && user?.rto?.status === 'paused' ? (
                          <>
                            RENT-TO-OWN<br />(RESUME)
                          </>
                        ) : (rtoDiscount.isRTOUser ? 'ALREADY ON RTO' : (isProPlan(user?.plan, user?.license_type) ? 'ALREADY PURCHASED' : 'RENT TO OWN'))}
                      </StyledAuthButton>
                    </div>
                    <p className="mt-2 text-center text-xs !text-base-gray">
                      Major upgrades (Density Pro v2) not included
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative box-border flex w-[100%] flex-col-reverse justify-between rounded-[30px] bg-[#FFFFFF0D] p-[48px] pb-[60px] text-center md:w-[100%] lg:flex-row">
                <div className="flex w-[100%] flex-col justify-between lg:w-[50%] lg:pb-[24px] lg:pr-[48px]">
                  <div className="flex flex-col pb-[24px] ">
                    <h2 className="pb-[8px] text-center font-['PPRightGrotesk-WideMedium'] text-[34px] font-black leading-[29.62px] !text-primary-purple lg:text-left">
                      {"Club/Festival Hardware"}
                    </h2>
                    {isBFSale() ? (
                      <p className="font-sm text-center font-inter text-base leading-[24px] tracking-wide !text-primary-purple lg:text-left">
                        <span style={{ textDecoration: 'line-through', color: 'red', opacity: 0.6 }}>${oldHardwarePrice}</span>
                        <span className="ml-2" style={{ color: 'green' }}>
                          {rtoDiscount.isRTOUser && rtoDiscount.discountAmount > 0
                            ? `$${(rtoDiscount.finalPrice || (350 - rtoDiscount.discountAmount)).toFixed(2)}`
                            : newHardwarePriceText
                          }
                        </span><br />
                        <span>
                          {rtoDiscount.isRTOUser && rtoDiscount.discountAmount > 0
                            ? `Fall Sale + RTO Discount Applied`
                            : (isUserPro ? 'Pro License Fall Sale Discount Applied' : 'Fall Sale Discount Applied')
                          }
                        </span><br />
                        {rtoDiscount.isRTOUser && rtoDiscount.discountAmount > 0 && (
                          <>
                            <span className="text-[12px]" style={{ opacity: 0.9 }}>
                              ($350 Fall Sale - ${rtoDiscount.discountAmount.toFixed(2)} RTO credit)
                            </span>
                            <br />
                          </>
                        )}
                        <span className="text-[12px]" style={{ opacity: 0.8 }}>
                          + Shipping
                        </span>
                      </p>
                    ) : (
                      (isUserPro && !rtoDiscount.isRTOUser) || (rtoDiscount.isRTOUser && rtoDiscount.discountAmount > 0) ? (
                        <p className="font-sm text-center font-inter text-base leading-[24px] tracking-wide !text-primary-purple lg:text-left">
                          <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>$450</span>
                          <span className="ml-2">
                            ${(450 - (rtoDiscount.isRTOUser && rtoDiscount.discountAmount > 0 ? rtoDiscount.discountAmount : (isUserPro ? 150 : 0))).toFixed(2)} - one time.
                          </span><br />
                          <span>
                            {rtoDiscount.isRTOUser && rtoDiscount.discountAmount > 0
                              ? `Rent-to-Own Discount Applied ($${rtoDiscount.discountAmount.toFixed(2)} - ${rtoDiscount.monthsPaid} ${rtoDiscount.monthsPaid === 1 ? 'month' : 'months'} paid)`
                              : 'Pro License Discount Applied'
                            }
                          </span>
                        </p>
                      ) : (
                        <p className="font-sm text-center font-inter text-base leading-[24px] tracking-wide !text-primary-purple lg:text-left">
                          $450 - one time
                        </p>
                      )
                    )}
                  </div>

                  <div className="h-[100%] pb-6 text-center font-inter text-sm font-medium leading-6 tracking-[0.0125rem] tracking-[0.0125rem] !text-base-gray lg:text-left">
                    <div className="pl-4">
                      <li className="disc-list">
                        Touring grade hardware to record sets
                      </li>
                      <li className="disc-list">
                        Lifetime license to Density Pro v1 without the watermark
                      </li>
                    </div>
                  </div>
                  <div className="w-[100%]">
                    <StyledAuthButton
                      ref={hardwareButtonRef}
                      onClick={handlePurchase}
                      variant={"contained"}
                      color="primary"
                      className="min-w-[100%]"
                      disabled={isHardwareButtonDisabled()}
                      sx={{
                        m: 0,
                        minWidth: "100%",
                        height: 44,
                        padding: "15px",
                        backgroundColor: "#c1c5dd",
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#020203",
                        "&:hover": {
                          backgroundColor: "#a8add1",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#666",
                          color: "#999",
                        }
                      }}
                    >
                      PURCHASE
                    </StyledAuthButton>
                    <p className="mt-2 text-center text-xs !text-base-gray">
                      Major upgrades (Density Pro v2) not included
                    </p>
                  </div>
                </div>
                <div className=" flex w-[100%] justify-center pb-[24px]  lg:w-[50%] lg:pb-0">
                  <img
                    className="hardware-density-image w-[380px] -translate-x-[20px] md:-translate-x-[40px] lg:min-w-[420px] lg:-translate-x-[24px]"
                    src={HardwareDensity}
                    alt=""
                  />
                </div>
              </div>
            </div>
          </section>

          <Footer />
        </main>
        {upgradeData.open && upgradeData.url && (
          <UpgradeModal
            error={""}
            open={upgradeData.open}
            url={upgradeData.url}
            handleClose={() => setUpgradeData({ ...upgradeData, open: false })}
            price={upgradeData.price}
            additionalText={upgradeData.additionalText}
            title={upgradeData.title}
          />
        )}

        <ConfirmRenewalModal
          open={renewalModalOpen}
          handleClose={() => setRenewalModalOpen(false)}
          handleRenewal={handleSubscriptionRenewal}
          subscriptionPrice={`$${subscriptions?.unit_price / 100}`}
          action={
            typeof subscriptions?.next_billing_at === "undefined"
              ? subscriptions?.status === "non_renewing"
                ? `Reactivate`
                : `Renew`
              : "Cancel"
          }
        />
      </StyledLandingPageWrapper>
    </>
  );
};
